import { useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { Loader2, AlertTriangle, Info } from 'lucide-react'
import { useMediaQuery } from '@/hooks/use-media-query'

interface CreateSnapshotModalProps {
  isOpen: boolean
  onClose: () => void
  period: { startDate: string; endDate: string }
  onSubmit: (data: { name?: string }) => void
  isSubmitting?: boolean
  hasData: boolean // EC10: false if no data for period
}

// Helper to format YYYY-MM-DD to DD/MM/YYYY without timezone issues
const formatDateString = (dateStr: string): string => {
  const [year, month, day] = dateStr.split('-')
  return `${day}/${month}/${year}`
}

export function CreateSnapshotModal({
  isOpen,
  onClose,
  period,
  onSubmit,
  isSubmitting,
  hasData,
}: CreateSnapshotModalProps) {
  const [name, setName] = useState('')
  const isDesktop = useMediaQuery('(min-width: 768px)')

  const defaultName = `Relatório ${new Date().toLocaleString('pt-BR')}`
  const displayName = name || defaultName

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    onSubmit({ name: name || undefined })
  }

  const Content = (
    <form onSubmit={handleSubmit} className="space-y-4">
      <div className="space-y-4">
        <div>
          <p className="text-sm font-medium mb-2">Período a consolidar:</p>
          <p className="text-sm text-muted-foreground">
            {formatDateString(period.startDate)} até {formatDateString(period.endDate)}
          </p>
        </div>

        <div className="space-y-2">
          <Label htmlFor="snapshot-name">Nome do relatório (opcional)</Label>
          <Input
            id="snapshot-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={defaultName}
            disabled={isSubmitting || !hasData}
          />
          <p className="text-xs text-muted-foreground">
            Se deixar vazio, será usado: {displayName}
          </p>
        </div>

        {hasData ? (
          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-sm">
              Este relatório consolida as métricas do período selecionado para comparação futura.
            </AlertDescription>
          </Alert>
        ) : (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              <p className="font-semibold mb-1">Nenhum dado para o período selecionado</p>
              <p className="text-sm">Importe dados antes de criar um relatório.</p>
            </AlertDescription>
          </Alert>
        )}
      </div>

      <div className="flex justify-end gap-2 pt-4 border-t">
        <Button type="button" variant="outline" onClick={onClose} disabled={isSubmitting}>
          Cancelar
        </Button>
        <Button type="submit" disabled={!hasData || isSubmitting}>
          {isSubmitting && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
          Criar Relatório
        </Button>
      </div>
    </form>
  )

  if (isDesktop) {
    return (
      <Dialog open={isOpen} onOpenChange={onClose}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle>Criar Relatório Consolidado</DialogTitle>
            <DialogDescription>
              Salve as métricas atuais para comparação futura
            </DialogDescription>
          </DialogHeader>
          {Content}
        </DialogContent>
      </Dialog>
    )
  }

  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent side="bottom" className="h-[90vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Criar Relatório Consolidado</SheetTitle>
          <SheetDescription>
            Salve as métricas atuais para comparação futura
          </SheetDescription>
        </SheetHeader>
        <div className="mt-6">{Content}</div>
      </SheetContent>
    </Sheet>
  )
}
